import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { baseurl } from "../../config/url";
import { useNavigate } from "react-router-dom";

/** ✅ Supports both adminToken + token (+ optional auth.token) */
const getAnyToken = () => {
  const adminToken = localStorage.getItem("adminToken");
  const token = localStorage.getItem("token");

  let authToken = null;
  try {
    const auth = JSON.parse(localStorage.getItem("auth") || "{}");
    authToken = auth?.token || null;
  } catch {}

  return adminToken || token || authToken || "";
};

// ✅ start of today in IST (as UTC Date for comparing ISO dates)
const startOfTodayIST = () => {
  const now = new Date();
  const istNow = new Date(now.getTime() + 330 * 60 * 1000); // shift to IST
  istNow.setHours(0, 0, 0, 0); // 00:00 IST
  return new Date(istNow.getTime() - 330 * 60 * 1000); // back to UTC
};

const NewUsers = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const token = useMemo(() => getAnyToken(), []);
  const headers = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchAllUsers = async () => {
      try {
        setLoading(true);
        setError("");

        if (!token) {
          setError("Token not found. Please login again.");
          navigate("/admin/login");
          return;
        }

        // ✅ Backend route you have: /api/user/all-users
        const res = await axios.get(`${baseurl}/api/user/all-users`, {
          headers,
          withCredentials: true,
        });

        if (!isMounted) return;

        const data = Array.isArray(res.data) ? res.data : res.data?.users || [];
        setAllUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching all users:", err.response?.data || err.message);

        if (err?.response?.status === 401) {
          setError("Session expired or unauthorized. Please login again.");
          navigate("/admin/login");
          return;
        }

        setError(err?.response?.data?.message || "Failed to fetch users");
        setAllUsers([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchAllUsers();

    return () => {
      isMounted = false;
    };
  }, [token, headers, navigate]);

  // ✅ Filter users created today (IST)
  const newUsers = useMemo(() => {
    const since = startOfTodayIST();

    return allUsers
      .filter((u) => {
        const d = u?.createdAt ? new Date(u.createdAt) : null;
        if (!d || Number.isNaN(d.getTime())) return false;
        return d >= since;
      })
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [allUsers]);

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <p className="text-lg font-semibold text-gray-600">Loading New Users...</p>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">New Users (Today)</h2>
            <p className="text-sm text-gray-500 mt-1">
              Data from <span className="font-mono">/api/user/all-users</span> (filtered in frontend).
            </p>
          </div>

          <div className="bg-white border rounded-lg px-4 py-2 text-sm text-gray-700">
            Count: <b>{newUsers.length}</b>
          </div>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3">
            {error}
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
          {newUsers.length === 0 ? (
            <div className="p-6 text-gray-600">No new users found today.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">ID</th>
                  <th className="px-4 py-3 text-left font-semibold">Name</th>
                  <th className="px-4 py-3 text-left font-semibold">Email</th>
                  <th className="px-4 py-3 text-left font-semibold">Mobile</th>
                  <th className="px-4 py-3 text-left font-semibold">Type</th>
                  <th className="px-4 py-3 text-left font-semibold">Created</th>
                  <th className="px-4 py-3 text-left font-semibold">Actions</th>
                </tr>
              </thead>

              <tbody>
                {newUsers.map((u) => {
                  const id = u._id || "-";
                  const name = u.name || u.fullName || "-";
                  const email = u.email || "-";
                  const mobile = u.mobile || "-";
                  const type = u.type || u.userType || "-";
                  const createdAt = u.createdAt
                    ? new Date(u.createdAt).toLocaleString("en-IN")
                    : "-";

                  return (
                    <tr key={id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-700">{id}</td>
                      <td className="px-4 py-3 font-semibold text-gray-900">{name}</td>
                      <td className="px-4 py-3 text-gray-800">{email}</td>
                      <td className="px-4 py-3 text-gray-800">{mobile}</td>
                      <td className="px-4 py-3 text-gray-700">{type}</td>
                      <td className="px-4 py-3 text-gray-600">{createdAt}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/admin/users/${id}`)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewUsers;